import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, Sparkles, HelpCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/api/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryTag = (category) => {
    const categoryStyles = {
      meet_me_monday: { bg: "bg-[#E63946]", text: "text-white", label: "Meet Me Monday" },
      trivia_tuesday: { bg: "bg-[#457B9D]", text: "text-white", label: "Trivia Tuesday" },
      wondering_wednesday: { bg: "bg-[#2A9D8F]", text: "text-white", label: "Wondering Wednesday" },
      think_thursday: { bg: "bg-[#9B59B6]", text: "text-white", label: "Think About It Thursday" },
      fun_fact_friday: { bg: "bg-[#E9C46A]", text: "text-[#1D3557]", label: "Fun Fact Friday" },
      storytelling_saturday: { bg: "bg-[#E76F51]", text: "text-white", label: "Storytelling Saturday" },
      simplicity_sunday: { bg: "bg-[#84A98C]", text: "text-white", label: "Simplicity Sunday" },
      general: { bg: "bg-[#1D3557]", text: "text-white", label: "General" }
    };
    
    const style = categoryStyles[category] || categoryStyles.general;
    return (
      <span className={`${style.bg} ${style.text} px-3 py-1 rounded-full font-bold text-sm border-2 border-[#1D3557]`}>
        {style.label}
      </span>
    );
  };

  const filteredPosts = filter === "all" 
    ? posts 
    : posts.filter(post => post.category === filter);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-12 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl sm:text-5xl font-black text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Farm Tales Blog
          </h1>
          <p className="text-lg text-[#1D3557] font-medium max-w-2xl mx-auto">
            Discover fun facts, trivia, and exciting stories from Marchfield Meadows!
          </p>
        </motion.div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-xl font-bold text-sm border-3 border-[#1D3557] transition-all ${
              filter === "all"
                ? "bg-[#1D3557] text-white shadow-[3px_3px_0px_0px_rgba(29,53,87,1)]"
                : "bg-white text-[#1D3557] hover:shadow-[3px_3px_0px_0px_rgba(29,53,87,1)]"
            }`}
            style={{ fontFamily: "'Fredoka', sans-serif" }}
            data-testid="filter-all-btn"
          >
            All
          </button>
          {[
            { key: "meet_me_monday", label: "Monday", color: "bg-[#E63946]" },
            { key: "trivia_tuesday", label: "Tuesday", color: "bg-[#457B9D]" },
            { key: "wondering_wednesday", label: "Wednesday", color: "bg-[#2A9D8F]" },
            { key: "think_thursday", label: "Thursday", color: "bg-[#9B59B6]" },
            { key: "fun_fact_friday", label: "Friday", color: "bg-[#E9C46A]" },
            { key: "storytelling_saturday", label: "Saturday", color: "bg-[#E76F51]" },
            { key: "simplicity_sunday", label: "Sunday", color: "bg-[#84A98C]" },
          ].map((cat) => (
            <button
              key={cat.key}
              onClick={() => setFilter(cat.key)}
              className={`px-4 py-2 rounded-xl font-bold text-sm border-3 border-[#1D3557] transition-all ${
                filter === cat.key
                  ? `${cat.color} ${cat.key === "fun_fact_friday" ? "text-[#1D3557]" : "text-white"} shadow-[3px_3px_0px_0px_rgba(29,53,87,1)]`
                  : "bg-white text-[#1D3557] hover:shadow-[3px_3px_0px_0px_rgba(29,53,87,1)]"
              }`}
              style={{ fontFamily: "'Fredoka', sans-serif" }}
              data-testid={`filter-${cat.key}-btn`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Posts Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-16 h-16 border-4 border-[#E63946] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredPosts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="tactile-card inline-block p-12">
              <h3 className="text-2xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                No posts yet!
              </h3>
              <p className="text-[#1D3557] font-medium">
                Check back soon for exciting farm tales and fun facts!
              </p>
            </div>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="tactile-card overflow-hidden"
                data-testid={`blog-post-card-${index}`}
              >
                {post.featured_image && (
                  <img
                    src={post.featured_image}
                    alt={post.title}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-6">
                  <div className="mb-3">
                    {getCategoryTag(post.category)}
                  </div>
                  <h2 className="text-xl font-bold text-[#1D3557] mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    {post.title}
                  </h2>
                  <p className="text-[#1D3557] font-medium mb-4 line-clamp-3">
                    {post.content.substring(0, 150)}...
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#1D3557]/70 font-medium flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(post.created_at)}
                    </span>
                    <Link
                      to={`/blog/${post.id}`}
                      className="text-[#E63946] font-bold flex items-center gap-1 hover:gap-2 transition-all"
                      data-testid={`read-more-${post.id}`}
                    >
                      Read More <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
